import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight, Clock, CalendarCheck, Phone } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { MobileActionBar } from "@/components/MobileActionBar";
import { Reveal } from "@/components/motion";
import { blogPosts, getPost } from "@/data/blogPosts";
import { DOCTOR, whatsappLink, SITE_URL } from "@/config/doctorConfig";

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPost(slug);

  if (!post) {
    return (
      <>
        <Header />
        <main className="pt-40 pb-32 bg-background min-h-screen text-center">
          <h1 className="font-serif font-bold text-3xl text-slate-900">Article not found</h1>
          <Link to="/blog" className="mt-4 inline-block text-teal-700 font-semibold" data-testid="blog-not-found-back">
            Back to all articles
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const idx = blogPosts.findIndex((p) => p.slug === slug);
  const prev = blogPosts[idx - 1];
  const next = blogPosts[idx + 1];
  const canonical = `${SITE_URL}/blog/${post.slug}`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    author: { "@type": "Physician", name: DOCTOR.name },
    publisher: { "@type": "Physician", name: DOCTOR.name },
    mainEntityOfPage: canonical,
  };

  return (
    <>
      <Helmet>
        <title>{`${post.title} | Dr. Suyash Bhadoriya, Bhopal`}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${post.title} | Dr. Suyash Bhadoriya`} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <Header />
      <main className="pt-28 lg:pt-36 pb-20 bg-background min-h-screen" data-testid="blog-post-page">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs font-mono tracking-wide text-slate-500 uppercase">
              <Link to="/" className="hover:text-teal-700 transition-colors">Home</Link>
              <span aria-hidden="true">/</span>
              <Link to="/blog" className="hover:text-teal-700 transition-colors">Blog</Link>
              <span aria-hidden="true">/</span>
              <span className="text-teal-700 truncate max-w-[160px] sm:max-w-none">{post.category}</span>
            </nav>
          </Reveal>
          <Reveal delay={1}>
            <h1 className="mt-6 font-serif font-bold text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight tracking-tight text-balance">
              {post.title}
            </h1>
            <div className="mt-5 flex items-center gap-4 text-xs text-slate-500">
              <span className="font-mono uppercase tracking-wider text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-3 py-1">{post.category}</span>
              <span className="inline-flex items-center gap-1"><Clock size={12} /> {post.readTime}</span>
              <span>Reviewed by {DOCTOR.name}, {DOCTOR.qualifications}</span>
            </div>
          </Reveal>

          <Reveal delay={2}>
            <div className="mt-10 space-y-8" data-testid="blog-post-content">
              {post.sections.map((s, i) => (
                <section key={i}>
                  {s.h && <h2 className="font-serif font-semibold text-2xl text-slate-900 mb-3">{s.h}</h2>}
                  {s.p?.map((para, j) => (
                    <p key={j} className="text-base text-slate-600 leading-relaxed mb-4 last:mb-0">{para}</p>
                  ))}
                  {s.list && (
                    <ul className="space-y-2.5">
                      {s.list.map((item, j) => (
                        <li key={j} className="flex gap-3 text-base text-slate-600 leading-relaxed">
                          <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-teal-600 shrink-0" aria-hidden="true" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>
          </Reveal>

          <Reveal delay={1}>
            <div className="mt-12 rounded-3xl bg-navy-900 p-7 sm:p-10 grain relative overflow-hidden" data-testid="blog-cta-box">
              <div className="relative">
                <h2 className="font-serif font-bold text-white text-2xl sm:text-3xl">Have a health concern?</h2>
                <p className="mt-3 text-sm sm:text-base text-slate-300 leading-relaxed max-w-lg">
                  Consult Dr. Suyash Bhadoriya, MD (Internal Medicine), at {DOCTOR.hospitalName}, Saket Nagar, Bhopal.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/#appointment" data-testid="blog-cta-book-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-navy-900 bg-white hover:bg-teal-50 transition-colors">
                    <CalendarCheck size={15} /> Book Appointment
                  </Link>
                  <a href={DOCTOR.clinicPhoneHref} data-testid="blog-cta-call-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white border border-white/25 hover:border-teal-300/60 transition-colors">
                    <Phone size={15} /> {DOCTOR.clinicPhoneDisplay}
                  </a>
                  <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="blog-cta-whatsapp-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white bg-[#128C7E] hover:bg-[#0d6e63] transition-colors">
                    <WhatsAppIcon size={15} /> WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          <p className="mt-8 text-xs text-slate-500 leading-relaxed">
            This article is for general educational purposes only and is not a substitute for an
            in-person medical consultation, diagnosis or treatment. In a medical emergency,
            contact emergency services or visit the nearest emergency department immediately.
          </p>

          <nav className="mt-10 pt-8 border-t border-slate-200 flex items-center justify-between gap-4" aria-label="More articles">
            {prev ? (
              <Link to={`/blog/${prev.slug}`} data-testid="blog-prev-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-navy-900 hover:text-teal-700 transition-colors max-w-[45%]">
                <ArrowLeft size={15} className="shrink-0 group-hover:-translate-x-1 transition-transform" />
                <span className="truncate">{prev.title}</span>
              </Link>
            ) : <span />}
            {next ? (
              <Link to={`/blog/${next.slug}`} data-testid="blog-next-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-navy-900 hover:text-teal-700 transition-colors max-w-[45%] text-right">
                <span className="truncate">{next.title}</span>
                <ArrowRight size={15} className="shrink-0 group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : <span />}
          </nav>
        </article>
      </main>
      <Footer />
      <MobileActionBar />
    </>
  );
}
